import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { User } from "@/lib/api/user";
import { isMenteeIdentity } from "@/lib/utils/system-roles";
import { Loader2 } from "lucide-react";

interface UserStatusToggleProps {
  user: User;
  onStatusChange: (deactivated: boolean) => void;
  isLoading: boolean;
  disabled?: boolean;
}

export function UserStatusToggle({
  user,
  onStatusChange,
  isLoading,
  disabled = false,
}: UserStatusToggleProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleStatusChange = () => {
    if (user.deactivated) {
      // If user is deactivated, reactivate without confirmation
      onStatusChange(false);
    } else {
      // If user is active, show confirmation before deactivating
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmDeactivation = () => {
    onStatusChange(true);
    setShowConfirmDialog(false);
  };

  const getStatusText = () => {
    return user.deactivated ? "Deactivated" : "Active";
  };

  const getButtonText = () => {
    return user.deactivated ? "Reactivate User" : "Deactivate User";
  };

  const getConfirmationMessage = () => {
    const userType = isMenteeIdentity(user.systemRoles) ? "teen" : "user";
    return `Are you sure you want to deactivate this ${userType}? All active mentorships will be deactivated.`;
  };

  return (
    <div className="mt-4 flex items-center justify-between">
      <div className="">
        <span className="text font-medium text-neutral-500">Status: </span>
        <span
          className={` ${user.deactivated ? "text-red-600" : "text-green-600"}`}
        >
          {getStatusText()}
        </span>
      </div>
      <Button
        variant={user.deactivated ? "default" : "destructive"}
        size="sm"
        onClick={handleStatusChange}
        disabled={isLoading || disabled}
        type="button"
      >
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {getButtonText()}
      </Button>

      <ConfirmActionDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmDeactivation}
        title="Confirm Deactivation"
        description={getConfirmationMessage()}
        loading={isLoading}
      />
    </div>
  );
}
